import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { LoaderCircle, Plus, X } from 'lucide-react';

export function Panel({ title, eyebrow, action, children, className = '' }: { title?: string; eyebrow?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={'panel ' + className}>
      {(title || action) && (
        <header className="panel-header">
          <div>
            {eyebrow && <span className="eyebrow">{eyebrow}</span>}
            {title && <h2>{title}</h2>}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function Button({ children, variant = 'default', className = '', loading = false, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'default' | 'primary' | 'danger' | 'ghost'; loading?: boolean }) {
  return (
    <button className={'button ' + variant + ' ' + className} disabled={props.disabled || loading} {...props}>
      {loading && <LoaderCircle className="spin" size={16} />}
      {children}
    </button>
  );
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && !error && <span className="field-hint">{hint}</span>}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="input" {...props} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className="input" {...props} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className="input" {...props} />;
}

export function Badge({ children, tone = 'muted' }: { children: ReactNode; tone?: 'good' | 'active' | 'warn' | 'danger' | 'muted' | 'gold' }) {
  return <span className={'badge badge-' + tone}>{children}</span>;
}

export function PageHeader({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action}
    </header>
  );
}

export function StatCard({ label, value, detail }: { label: string; value: ReactNode; detail?: string }) {
  return (
    <div className="stat-card">
      <span>{label}</span>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </div>
  );
}

export function LoadingBlock({ label = '正在读取数据' }: { label?: string }) {
  if (label === '正在读取数据') return <div className="skeleton-list">{[1,2,3,4].map(i => <div className="skeleton-row" key={i} />)}</div>;
  return <div className="state-block"><LoaderCircle className="spin" size={22} /><span>{label}</span></div>;
}

export function EmptyBlock({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return <div className="empty-block"><strong>{title}</strong>{description && <span>{description}</span>}{action && <div className="empty-action">{action}</div>}</div>;
}

export function ActionSheet({ open, title, eyebrow, description, onClose, children }: { open: boolean; title: string; eyebrow?: string; description?: string; onClose: () => void; children: ReactNode }) {
  if (!open) return null;
  return (
    <>
      <button className="sheet-scrim" onClick={onClose} aria-label="关闭表单" />
      <section className="action-sheet" aria-modal="true" role="dialog">
        <header className="action-sheet-head">
          <div>{eyebrow && <span className="eyebrow">{eyebrow}</span>}<h2>{title}</h2>{description && <p>{description}</p>}</div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="关闭"><X size={20} /></button>
        </header>
        <div className="action-sheet-body">{children}</div>
      </section>
    </>
  );
}

export function Fab({ label, onClick }: { label: string; onClick: () => void }) {
  return <button type="button" className="fab" onClick={onClick} aria-label={label} title={label}><Plus size={24} /><span>{label}</span></button>;
}
